import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  FiDollarSign,
  FiAlertCircle,
  FiHelpCircle,
  FiX,
  FiPlus,
  FiCheck,
} from "react-icons/fi";
import {
  setPriceotherDetails,
  setpricingScooring,
  setpropertyScooring,
  setPostPropertyStepsStatus,
} from "../../../redux/property/PropertySlice";

// Helper to convert number to Indian words
const convertToIndianWords = (numStr) => {
  const num = parseInt(numStr, 10);
  if (isNaN(num) || num <= 0) return "";
  if (num >= 10000000) {
    const cr = (num / 10000000).toFixed(2).replace(/\.00$/, "");
    return `${cr} Crore`;
  }
  if (num >= 100000) {
    const lk = (num / 100000).toFixed(2).replace(/\.00$/, "");
    return `${lk} Lakh`;
  }
  if (num >= 1000) {
    const th = (num / 1000).toFixed(1).replace(/\.0$/, "");
    return `${th} Thousand`;
  }
  return `${num}`;
};

const StepThreePricing = ({ onBack, onNext }) => {
  const dispatch = useDispatch();
  const { PriceotherDetails, PhotoDetails } = useSelector(
    (state) => state.Property
  );

  const [ownership, setOwnership] = useState("Co-operative society");
  const [expectedPrice, setExpectedPrice] = useState("122222");
  const [priceNegotiable, setPriceNegotiable] = useState(true);

  // Additional Pricing
  const [showMorePricing, setShowMorePricing] = useState(false);
  const [maintenanceCharges, setMaintenanceCharges] = useState("222");
  const [maintenancePeriod, setMaintenancePeriod] = useState("Monthly");
  const [bookingAmount, setBookingAmount] = useState("");

  // Description / Unique Details
  const defaultDesc =
    "The Beautiful 3 BHK Flat Is Situated In Banjara Hills, Hyderabad, In A Popular Residential Society. Containing 3 Bedrooms, 3 Bathrooms, And 1 Balcony. This Flat Is Spread Over A Carpet Area Of 1625 Sq Ft. The Residential Building Has Five Floors, And The Flat For Sale Is Located On The First Floor...";

  const [description, setDescription] = useState(defaultDesc);
  const [showAlert, setShowAlert] = useState(
    !PhotoDetails || PhotoDetails.length === 0
  );
  const [errorMsg, setErrorMsg] = useState("");

  // Prefill
  useEffect(() => {
    if (PriceotherDetails) {
      if (PriceotherDetails.typeOfOwnerShip)
        setOwnership(PriceotherDetails.typeOfOwnerShip);
      if (PriceotherDetails.expectedPrice)
        setExpectedPrice(PriceotherDetails.expectedPrice);
      if (PriceotherDetails.priceNegotiable !== undefined)
        setPriceNegotiable(PriceotherDetails.priceNegotiable);
      if (PriceotherDetails.maintenance)
        setMaintenanceCharges(PriceotherDetails.maintenance);
      if (PriceotherDetails.uniquePropertyText)
        setDescription(PriceotherDetails.uniquePropertyText);
    }
  }, [PriceotherDetails]);

  // Scoring
  useEffect(() => {
    let score = 0;
    if (ownership) score += 6;
    if (expectedPrice && parseInt(expectedPrice, 10) > 0) score += 8;
    if (description && description.trim().length >= 30) score += 6;
    dispatch(setpricingScooring(score));
    dispatch(setpropertyScooring());
  }, [ownership, expectedPrice, description]);

  const handleContinue = () => {
    setErrorMsg("");
    if (!expectedPrice || parseInt(expectedPrice, 10) <= 0) {
      setErrorMsg("Please enter a valid expected price.");
      return;
    }
    if (!description || description.trim().length < 30) {
      setErrorMsg("Please enter at least 30 characters in property description.");
      return;
    }

    const pricingData = {
      typeOfOwnerShip: ownership,
      expectedPrice: expectedPrice,
      priceNegotiable: priceNegotiable,
      maintenance: maintenanceCharges,
      maintenanceMonthly: maintenancePeriod,
      bookingAmount: bookingAmount,
      uniquePropertyText: description,
    };

    dispatch(setPriceotherDetails(pricingData));
    dispatch(setPostPropertyStepsStatus(5)); // Step 5 Amenities & Review
    if (onNext) onNext();
  };

  const handleGoBack = () => {
    dispatch(setPostPropertyStepsStatus(3)); // Back to Photos
    if (onBack) onBack();
  };

  return (
    <div className="mgh-step-three">
      <div className="mgh-post-card">
        {/* Banner Header */}
        <div className="mgh-post-banner-header">
          <div className="mgh-post-banner-icon">
            <span style={{ fontWeight: 800 }}>₹</span>
          </div>
          <div>
            <h2 className="mgh-post-banner-title">
              Add pricing and details...
            </h2>
            <p className="mgh-post-banner-sub">
              Set clear pricing to attract serious and qualified buyers.
            </p>
          </div>
        </div>

        {/* Low Response Warning Banner */}
        {showAlert && (
          <div
            className="mgh-warning-alert"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <FiAlertCircle className="mgh-warning-alert-icon" />
              <div className="mgh-warning-alert-text">
                You might get <strong>Low responses</strong>, as your listing has no photos.
                Rank up your listing by adding pictures.
              </div>
            </div>
            <button
              type="button"
              onClick={() => setShowAlert(false)}
              style={{
                background: "none",
                border: "none",
                color: "#92400e",
                cursor: "pointer",
                fontSize: "18px",
              }}
            >
              <FiX />
            </button>
          </div>
        )}

        {/* 2-Column Grid: Pricing & Details vs Unique Description */}
        <div className="mgh-pricing-grid">
          {/* Left Column: Ownership & Price Details */}
          <div>
            {/* Ownership */}
            <div style={{ marginBottom: "22px" }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  marginBottom: "8px",
                }}
              >
                <label className="mgh-form-label" style={{ margin: 0 }}>
                  Ownership
                </label>
                <FiHelpCircle style={{ color: "#94a3b8", fontSize: "14px" }} />
              </div>
              <div className="mgh-pills-wrap">
                {[
                  "Freehold",
                  "Leasehold",
                  "Co-operative society",
                  "Power of Attorney",
                ].map((item) => (
                  <button
                    type="button"
                    key={item}
                    className={`mgh-pill-btn ${ownership === item ? "active" : ""}`}
                    onClick={() => setOwnership(item)}
                  >
                    {item}
                  </button>
                ))}
              </div>
            </div>

            {/* Price Details */}
            <div style={{ marginBottom: "20px" }}>
              <label className="mgh-form-label">
                Price Details <span className="required">*</span>
              </label>

              <div className="mgh-ppt-input-wrap">
                <span
                  className="mgh-ppt-icon"
                  style={{ fontWeight: 700, fontSize: "16px" }}
                >
                  ₹
                </span>
                <input
                  type="number"
                  className="mgh-ppt-input"
                  placeholder="Expected Price (e.g. 5000000)"
                  value={expectedPrice}
                  onChange={(e) => setExpectedPrice(e.target.value)}
                />
              </div>

              {expectedPrice && (
                <div className="mgh-words-helper">
                  ₹ {convertToIndianWords(expectedPrice)}
                </div>
              )}

              {/* Price Negotiable Checkbox */}
              <label className="mgh-checkbox-wrap">
                <input
                  type="checkbox"
                  className="mgh-checkbox"
                  checked={priceNegotiable}
                  onChange={(e) => setPriceNegotiable(e.target.checked)}
                />
                <span style={{ fontSize: "13.5px", fontWeight: 600, color: "#334155" }}>
                  Price Negotiable
                </span>
              </label>
            </div>

            {/* Additional Pricing Details */}
            <div style={{ marginBottom: "16px" }}>
              <label className="mgh-form-label">
                Additional Pricing Details (Optional)
              </label>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 130px", gap: "10px", marginBottom: "10px" }}>
                <div className="mgh-ppt-input-wrap">
                  <input
                    type="number"
                    className="mgh-ppt-input"
                    placeholder="Maintenance charges"
                    value={maintenanceCharges}
                    onChange={(e) => setMaintenanceCharges(e.target.value)}
                  />
                </div>
                <div className="mgh-ppt-input-wrap">
                  <select
                    className="mgh-ppt-select"
                    value={maintenancePeriod}
                    onChange={(e) => setMaintenancePeriod(e.target.value)}
                  >
                    <option value="Monthly">Monthly</option>
                    <option value="Annually">Annually</option>
                    <option value="Quarterly">Quarterly</option>
                  </select>
                </div>
              </div>

              {showMorePricing && (
                <div style={{ marginTop: "10px" }}>
                  <div className="mgh-ppt-input-wrap">
                    <input
                      type="number"
                      className="mgh-ppt-input"
                      placeholder="Booking / Token amount"
                      value={bookingAmount}
                      onChange={(e) => setBookingAmount(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <button
                type="button"
                style={{
                  background: "none",
                  border: "none",
                  color: "#ea580c",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer",
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  padding: "4px 0",
                  marginTop: "6px",
                }}
                onClick={() => setShowMorePricing(!showMorePricing)}
              >
                <FiPlus /> {showMorePricing ? "Hide additional details" : "Add more pricing details"}
              </button>
            </div>
          </div>

          {/* Right Column: Unique Description */}
          <div>
            <div style={{ marginBottom: "8px" }}>
              <h4 style={{ fontSize: "15px", fontWeight: 700, margin: 0, color: "#0f172a" }}>
                What makes your property unique
              </h4>
              <p style={{ fontSize: "12.5px", color: "#ea580c", margin: "4px 0 0", fontWeight: 600 }}>
                Please Enter Unique Property Details
              </p>
              <p style={{ fontSize: "12px", color: "#64748b", margin: "2px 0 10px" }}>
                Adding description will increase your listing visibility.{" "}
                <span style={{ color: "#ea580c", cursor: "pointer", fontWeight: 600 }}>
                  Review
                </span>
              </p>
            </div>

            <textarea
              className="mgh-textarea"
              rows={7}
              placeholder="Tell buyers why they will love living here... (e.g. Spacious corner flat with natural lighting, modular kitchen, near top schools and metro)"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />

            <div className="mgh-char-counter">
              <span
                style={{
                  color: description.length >= 30 ? "#10b981" : "#ef4444",
                  fontWeight: 600,
                }}
              >
                {description.length}/5000 (Min. 30 characters)
              </span>
            </div>

            {errorMsg && (
              <div
                style={{
                  color: "#ef4444",
                  fontSize: "13px",
                  fontWeight: 600,
                  marginTop: "8px",
                }}
              >
                {errorMsg}
              </div>
            )}
          </div>
        </div>

        {/* Action Buttons */}
        <div className="mgh-form-actions">
          <button
            type="button"
            className="mgh-btn-secondary"
            onClick={handleGoBack}
          >
            Go Back
          </button>
          <button
            type="button"
            className="mgh-btn-primary"
            onClick={handleContinue}
          >
            Continue
          </button>
        </div>
      </div>
    </div>
  );
};

export default StepThreePricing;
